import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import CodeEditor from './components/Editor/CodeEditor';
import MainCalculatorComponent from './components/MainCalculator';
import LatexRenderer from './components/Output/LatexRenderer';
import OutputControls from './components/Output/OutputControls';
import EditorControls from './components/Editor/EditorControls';
import AboutPage from './pages/AboutPage';


import Toast from './components/Common/Toast';
import './App.css';

import LandingPage from './pages/LandingPage';  

function MainCalculator() {
  const { dslInput, setDslInput } = useAppContext();

  return (
    <div className="app-container">
      {/* Editor Panel */}
      <div className="editor-panel">
        <div className="panel-header">
          <h2>🔢 ./math Input</h2>
          <EditorControls />
        </div>
        <CodeEditor 
          value={dslInput} 
          onChange={setDslInput}
          placeholder="Enter your ./math expressions here..."
        />
      </div>

      {/* Output Panel */}
      <div className="output-panel">
        <div className="panel-header">
          <h2>📊 LaTeX Output</h2>
          <OutputControls />
        </div>
        <LatexRenderer />
      </div>
    </div>
  );
}

function App() {
  const [dslInput, setDslInput] = useState(`add(mul(2, x), pow(y, 3))

matrix(covector(1, 2), covector(3, 4))

jacobian(vector(mul(x, y), add(x, y)), vector(x, y))

eq(pow(x, 2) | add(mul(2, x), 1))

text("Use curly brackets for multiline expressions")

{
  add(
    matrix(
      covector(a, b),
      covector(c, d)
    ),
    matrix(
      covector(e, f),
      covector(g, h)
    )
  )
}`);
  
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [latexOutput, setLatexOutput] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Toast state management
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  
  const showToast = (message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    // Clear toast after 3 seconds
    setTimeout(() => {
      setToastMessage('');
    }, 3000);
  };

  return (
    <AppProvider value={{ 
      dslInput, 
      setDslInput, 
      isLiveMode, 
      setIsLiveMode, 
      latexOutput, 
      setLatexOutput, 
      isProcessing, 
      setIsProcessing, 
      showToast 
    }}>
      <Router>
        <div className="app">
          {/* MathLatech Banner with Navigation */}
          <div className="mathlatech-banner">
            <h1>
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                MathLatech <span className="beta-tag">(Beta Version)</span>
              </Link>
            </h1>
            <nav className="banner-nav">
              <Link to="/about" className="nav-link">
                <span className="nav-icon">ℹ️</span>
                <span className="nav-text">About</span>
              </Link>
              <a href="/dotslashmath-manual (1).html" className="nav-link" target="_blank" rel="noopener noreferrer">
                <span className="nav-icon">📚</span>
                <span className="nav-text">Docs</span>
              </a>
              <Link to="/notebook" className="nav-link">
                <span className="nav-icon">📓</span>
                <span className="nav-text">Notebook</span>
              </Link>
              <a href="mailto:dotslashmath@gmail.com" className="nav-link">
                <span className="nav-icon">📧</span>
                <span className="nav-text">Contact</span>
              </a>
            </nav>
          </div>

          {/* Page Content */}
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/app" element={<MainCalculatorComponent />} />
            <Route path="/about" element={<AboutPage />} />

            <Route path="/notebook" element={
              <div
                style={{
                  width: '100%',
                  height: '100vh',
                  paddingTop: '50px',                  // offset for fixed banner
                  background: 'var(--bg-primary)',
                }}
              >
                <iframe
                  src="/notebook-prototype-v1.html"
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 50px)',      // fill below the banner
                    border: 'none',
                    display: 'block',
                  }}
                  title="Math Notebook"
                />
              </div>
            } />
          </Routes>
          
          {/* Toast Component */}
          {toastMessage && (
            <Toast 
              message={toastMessage} 
              type={toastType} 
            />
          )}
        </div>
      </Router>
    </AppProvider>
  );
}

export default App;// Updated for deployment
